// SecureImg / SecureLink — drop-in replacements for <img> and <a> that show private documents
// through 10-minute links (services/secureFiles.js). Anything that is not one of our private
// documents (a product photo, a logo, a preview of a file just chosen) is shown exactly as before.
import { useEffect, useRef, useState } from 'react';
import { isPrivateFile, secureLink } from '../services/secureFiles';

/** <img>, with the link fetched only when the picture is near the screen (a 300-page statement
 *  does not ask for 300 links at once). */
export function SecureImg({ src, alt = '', ...rest }) {
  const priv = isPrivateFile(src);
  const [url, setUrl] = useState(priv ? null : src);
  const ref = useRef(null);
  useEffect(() => {
    if (!priv) { setUrl(src); return undefined; }
    setUrl(null);
    let alive = true;
    const go = () => secureLink(src).then((u) => { if (alive) setUrl(u); });
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') { go(); return () => { alive = false; }; }
    const io = new IntersectionObserver((entries) => { if (entries.some((e) => e.isIntersecting)) { io.disconnect(); go(); } }, { rootMargin: '600px' });
    io.observe(el);
    return () => { alive = false; io.disconnect(); };
  }, [src, priv]);
  // Until the link arrives the picture is an empty box of the same shape (no broken-image icon).
  // eslint-disable-next-line jsx-a11y/alt-text
  return <img ref={ref} alt={alt} {...rest} src={url || 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw=='} />;
}

/** <a> to a document: the fresh link is fetched at the moment of the click. */
export function SecureLink({ href, children, onClick, ...rest }) {
  const open = async (e) => {
    if (onClick) onClick(e);
    if (!isPrivateFile(href) || e.defaultPrevented) return;
    e.preventDefault();
    const w = window.open('', rest.target || '_blank'); // opened now, so the browser does not block it
    const u = await secureLink(href);
    if (w) w.location = u; else window.location.assign(u);
  };
  return <a href={isPrivateFile(href) ? '#' : href} {...rest} onClick={open}>{children}</a>;
}
